import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ChangeDetectionStrategy, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { FeedbackApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/feedback/feedback-api';
import { BaseModalComponent } from '../../ui-components/modal/base-modal-component';
import { SharedModule } from '../../ui-components/shared.module';
import { ChoiceOption } from '../../ui-components/choice-selector/choice-selector.component';

export type ReportChoice = 'wrong' | 'missing' | 'inappropriate' | 'other';
/** Les quatre motifs de signalement, dans l'ordre de la feuille. */
export const REPORT_CHOICES: ReportChoice[] = ['wrong', 'missing', 'inappropriate', 'other'];

/** Type d'avis enregistré pour un signalement de Willy (filtrable dans l'admin « Retours »). */
export const WILLY_REPORT_TYPE = 'willy_report';
/**
 * Borne du commentaire enregistré. Le serveur n'en impose pas (`comment` est un
 * `text`, `type` un varchar(50)) : on garde la réponse citée raisonnable.
 */
const COMMENT_MAX_LENGTH = 1000;

/**
 * Signaler une réponse de Willy (P5.1.2), en feuille du bas.
 *
 * Avant, « Envoyer » fermait la fenêtre sans rien envoyer (`TODO: Send report
 * to backend`). Le signalement part maintenant comme un retour
 * (`FeedbackApi`, type `willy_report`) : le motif, la précision et le début de
 * la réponse signalée (1000 caractères au plus).
 *
 * Le motif est obligatoire (le bouton le dit), la précision facultative. Un
 * refus du serveur est dit dans la feuille, qui reste ouverte.
 */
@Component({
  selector: 'vw-report-modal',
  imports: [SharedModule, TranslatePipe, FormsModule],
  templateUrl: './report-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [{ provide: FeedbackApi, useClass: FeedbackApi }],
})
export class ReportModalComponent extends BaseModalComponent implements OnChanges {
  private readonly feedbackApi = inject(FeedbackApi);

  /** Texte de la réponse signalée, joint au signalement pour savoir de quoi il parle. */
  @Input() answer: string | null = null;
  @Output() onReport = new EventEmitter<void>();

  readonly choices = REPORT_CHOICES;
  readonly choiceOptions: ChoiceOption[] = this.choices.map((c) => ({ value: c, label: 'common.modals.report.choices.' + c }));
  readonly messageMaxLength = 200;

  selectedChoice: ReportChoice | null = null;
  message = '';
  choiceError = false;
  sending = false;
  sendFailed = false;

  ngOnChanges(changes: SimpleChanges) {
    // Chaque ouverture repart d'une feuille vierge.
    if (changes['isOpen'] && this.isOpen) {
      this.selectedChoice = null;
      this.message = '';
      this.choiceError = false;
      this.sendFailed = false;
    }
  }

  onChoiceSelected(choice: ReportChoice) {
    this.selectedChoice = choice;
    this.choiceError = false;
  }

  /** Le commentaire enregistré : « [motif] précision | Réponse : … », coupé à 1000. */
  buildComment(): string {
    const parts = [`[${this.selectedChoice}]`];
    const detail = (this.message ?? '').trim();
    if (detail) parts.push(detail);
    const answer = (this.answer ?? '').trim().replace(/\s+/g, ' ');
    if (answer) parts.push(`| Réponse : ${answer}`);
    return parts.join(' ').slice(0, COMMENT_MAX_LENGTH);
  }

  async onSendButtonClick() {
    if (this.sending) return;
    if (!this.selectedChoice) {
      this.choiceError = true;
      return;
    }
    this.sending = true;
    this.sendFailed = false;
    try {
      await this.feedbackApi.create({ type: WILLY_REPORT_TYPE, comment: this.buildComment(), origin: '/willy' });
      this.close();
      this.onReport.emit();
    } catch (e) {
      console.error('ReportModalComponent: failed to send report', e);
      this.sendFailed = true;
    } finally {
      this.sending = false;
    }
  }
}
